/** Espera curta após o hero pintar antes de carregar recursos não críticos (não compete com LCP). */
const HERO_IDLE_TIMEOUT_MS = 800

/** Garante execução mesmo se o observer falhar. */
const HERO_DEFER_MAX_MS = 4000

function scheduleIdle(callback: () => void): () => void {
  const w = window as Window & {
    requestIdleCallback?: (cb: IdleRequestCallback, opts?: { timeout: number }) => number
    cancelIdleCallback?: (id: number) => void
  }

  if (typeof w.requestIdleCallback === "function") {
    const id = w.requestIdleCallback(() => callback(), { timeout: HERO_IDLE_TIMEOUT_MS })
    return () => w.cancelIdleCallback?.(id)
  }

  const id = window.setTimeout(callback, HERO_IDLE_TIMEOUT_MS)
  return () => clearTimeout(id)
}

/**
 * Adia um callback até o `#hero` estar visível (ou ausente) + idle curto.
 * Fallback em {@link HERO_DEFER_MAX_MS} para páginas sem hero ou observer indisponível.
 */
export function waitUntilAfterHero(onReady: () => void): () => void {
  if (typeof window === "undefined") return () => {}

  let cancelled = false
  let ran = false
  const cleanups: (() => void)[] = []

  const run = () => {
    if (cancelled || ran) return
    ran = true
    onReady()
  }

  const startHeroGate = () => {
    if (cancelled || ran) return

    const hero = document.getElementById("hero")
    if (!hero) {
      cleanups.push(scheduleIdle(run))
      return
    }

    const heroAlreadyVisible = () => {
      const rect = hero.getBoundingClientRect()
      return rect.bottom > 0 && rect.top < window.innerHeight
    }

    const afterHeroPaint = () => {
      if (cancelled || ran) return
      cleanups.push(scheduleIdle(run))
    }

    if (heroAlreadyVisible()) {
      afterHeroPaint()
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return
        observer.disconnect()
        afterHeroPaint()
      },
      { threshold: 0.01 },
    )
    observer.observe(hero)
    cleanups.push(() => observer.disconnect())
  }

  const maxTimer = window.setTimeout(run, HERO_DEFER_MAX_MS)
  cleanups.push(() => clearTimeout(maxTimer))

  const startWhenDomReady = () => {
    if (cancelled || ran) return
    startHeroGate()
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", startWhenDomReady, { once: true })
    cleanups.push(() => document.removeEventListener("DOMContentLoaded", startWhenDomReady))
  } else {
    startWhenDomReady()
  }

  return () => {
    cancelled = true
    for (const cleanup of cleanups) cleanup()
  }
}
